/**
 * MobileUpdateService — polls the custom update server and triggers an
 * in-app update flow for Android and iOS.
 *
 * ### Android
 * The service downloads the APK from the update server and uses the
 * Capacitor `Browser` plugin to open the APK URL, which triggers the
 * system package installer.  (Requires "Install unknown apps" permission
 * on the target device, or the APK can be side-loaded via ADB in dev.)
 *
 * ### iOS
 * Apple does not allow third-party sideloading outside of the App Store /
 * TestFlight.  The service shows a notification and opens the App Store URL
 * (or a TestFlight link for internal builds) in the system browser.
 *
 * ### Strategy pattern
 * `checkForMobileUpdate()` resolves with an `UpdateInfo` when a newer
 * version is available, or `null` when the app is up-to-date.
 * The caller (`MobileUpdateModal`) handles UI presentation.
 */

const UPDATE_SERVER =
  (typeof process !== "undefined" && process.env?.REACT_APP_UPDATE_SERVER) ||
  "https://web.eunosoft.com";

export interface MobileUpdateInfo {
  version: string;
  releaseNotes: string;
  /** Direct APK URL (Android) or App Store URL (iOS). */
  downloadUrl: string;
  /** 'android' | 'ios' */
  platform: "android" | "ios";
}

/**
 * Fetch the latest version from the update server and compare with the
 * currently installed version from `@capacitor/app`.
 */
export async function checkForMobileUpdate(): Promise<MobileUpdateInfo | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { App } = await import("@capacitor/app") as any;
    const info = await App.getInfo();
    const currentVersion = info.version;

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { Device } = await import("@capacitor/device") as any;
    const deviceInfo = await Device.getInfo();
    const mobilePlatform =
      deviceInfo.platform === "ios" ? "ios" : "android";

    const resp = await fetch(
      `${UPDATE_SERVER}/api/updates/${mobilePlatform}/latest`,
      { cache: "no-store" }
    );
    if (!resp.ok) return null;

    const data: {
      version: string;
      releaseNotes: string;
      downloadUrl: string;
    } = await resp.json();

    if (isNewerVersion(data.version, currentVersion)) {
      return { ...data, platform: mobilePlatform };
    }
    return null;
  } catch (e) {
    console.warn("Mobile update check failed:", e);
    return null;
  }
}

/**
 * Start the update install.
 * Saves the release notes to Preferences first so they can be shown as a
 * "What's New" screen on the next app open (after the update completes).
 * - Android: open the APK URL in Capacitor Browser (triggers installer).
 * - iOS: open the App Store / TestFlight URL in Safari.
 */
export async function installMobileUpdate(
  info: MobileUpdateInfo
): Promise<void> {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { Browser } = await import("@capacitor/browser") as any;
  await Browser.open({ url: info.downloadUrl });
}

const WHATS_NEW_KEY = "mobile_whats_new_pending";

export interface WhatsNewData {
  version: string;
  notes: string;
}

/** Persist release notes before triggering the update installer. */
async function saveWhatsNew(version: string, notes: string): Promise<void> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { Preferences } = await import("@capacitor/preferences") as any;
    await Preferences.set({
      key: WHATS_NEW_KEY,
      value: JSON.stringify({ version, notes }),
    });
  } catch {
    // Non-fatal — best effort
  }
}

/**
 * On app startup: read and clear the pending "What's New" payload.
 * Returns the data if present, null otherwise.
 */
export async function loadAndClearWhatsNew(): Promise<WhatsNewData | null> {
  try {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const { Preferences } = await import("@capacitor/preferences") as any;
    const result = await Preferences.get({ key: WHATS_NEW_KEY });
    if (!result?.value) return null;
    await Preferences.remove({ key: WHATS_NEW_KEY });
    return JSON.parse(result.value) as WhatsNewData;
  } catch {
    return null;
  }
}

/** Semver-aware comparison: true if `a` is strictly newer than `b`. */
function isNewerVersion(a: string, b: string): boolean {
  const toNum = (v: string) =>
    v
      .split(".")
      .map(Number)
      .reduce((acc, n) => acc * 1000 + n, 0);
  return toNum(a) > toNum(b);
}
